import { useState, useCallback, useMemo } from 'react';

/**
 * Hook that builds the shared card handler props object
 * Used by both standard columns and swimlane modes
 */
const useCardHandlerProps = ({
  // Data
  categories,
  allTags,
  allTasks,
  nestedSprints,
  allSprints = [],
  statuses,
  cardDisplaySettings,
  lockStates,
  isTemplate = false,
  // Selection
  handleTaskClick,
  // Card actions
  onOpenDetails,
  onOpenAttachments,
  onCategoryChange,
  onRenameCategory,
  onDeleteCategory,
  onAddTag,
  onRemoveTag,
  onSetPrimaryTag,
  onRenameTag,
  onDeleteTag,
  onOpenNestedSprint,
  onCreateNestedSprint,
  onRemoveNestedSprint,
  onIconChange,
  onRename,
  onStatusChange,
  onDueDateChange,
  onRemindersChange,
  onEstimatedDurationMinutesChange,
  onCreatedAtChange,
  onStartedAtChange,
  onCompletedAtChange,
  onUpdatedAtChange,
  onPriorityChange,
  onDifficultyChange,
  onCustomFieldValueChange,
  customFields,
  onDeleteTask,
  onDuplicateTask,
  onCreateParallelMission,
  onInsertMissionBefore,
  onSetStatusAutoSync,
  onDeliverablesChange,
  onInputsChange,
  // Archive
  onArchiveTask,
  onUnarchiveTask,
  // Dependencies
  onDependencyChange,
  // Decision nodes
  onDecisionAnswerChange,
  onDecisionOptionsChange,
  // Clipboard
  copyCard,
  duplicateCard,
  // Status management (for status dropdown in cards)
  onAddStatus,
  onDeleteStatus,
  onRenameStatus,
  // View navigation (for "Open in view" button)
  currentView,
  customViews,
  activeCustomViewId,
  onViewChange,
  onSelectCustomView,
  onGoToTaskInView,
  // Deliverables warning modal
  onSubmitDeliverable,
  // Deliverables editing (for conditions modal)
  onEditDeliverables,
  onClearDeliverable,
  onNavigateToParentMission,
}) => {
  // Track which card should be in edit mode (for newly created cards)
  const [editingCardId, setEditingCardId] = useState(null);

  const handleFinishTitleEdit = useCallback(() => {
    setEditingCardId(null);
  }, []);

  const cardHandlerProps = useMemo(() => ({
    categories,
    allTags,
    allTasks,
    nestedSprints,
    allSprints,
    statuses,
    cardDisplaySettings,
    lockStates,
    isTemplate,
    onTaskClick: handleTaskClick,
    onOpenDetails,
    onOpenAttachments,
    onCategoryChange,
    onRenameCategory,
    onDeleteCategory,
    onAddTag,
    onRemoveTag,
    onSetPrimaryTag,
    onRenameTag,
    onDeleteTag,
    onOpenNestedSprint,
    onCreateNestedSprint,
    onRemoveNestedSprint,
    onIconChange,
    onRename,
    onStatusChange,
    onDueDateChange,
    onRemindersChange,
    onEstimatedDurationMinutesChange,
    onCreatedAtChange,
    onStartedAtChange,
    onCompletedAtChange,
    onUpdatedAtChange,
    onPriorityChange,
    onDifficultyChange,
    onCustomFieldValueChange,
    customFields,
    onDeleteTask,
    onDuplicateTask,
    onCreateParallelMission,
    onInsertMissionBefore,
    onSetStatusAutoSync,
    onDeliverablesChange,
    onInputsChange,
    onArchiveTask,
    onUnarchiveTask,
    onDependencyChange,
    onDecisionAnswerChange,
    onDecisionOptionsChange,
    onCopyCard: copyCard,
    onDuplicateCard: duplicateCard,
    onAddStatus,
    onDeleteStatus,
    onRenameStatus,
    editingCardId,
    onFinishTitleEdit: handleFinishTitleEdit,
    currentView,
    customViews,
    activeCustomViewId,
    onViewChange,
    onSelectCustomView,
    onGoToTaskInView,
    onSubmitDeliverable,
    onEditDeliverables,
    onClearDeliverable,
    onNavigateToParentMission,
  }), [
    categories, allTags, allTasks, nestedSprints, allSprints, statuses, cardDisplaySettings, lockStates, isTemplate,
    handleTaskClick, onOpenDetails, onOpenAttachments, onCategoryChange,
    onRenameCategory, onDeleteCategory, onAddTag, onRemoveTag,
    onSetPrimaryTag, onRenameTag, onDeleteTag, onOpenNestedSprint,
    onCreateNestedSprint, onRemoveNestedSprint, onIconChange, onRename,
    onStatusChange, onDueDateChange, onRemindersChange, onEstimatedDurationMinutesChange,
    onCreatedAtChange, onStartedAtChange, onCompletedAtChange,
    onUpdatedAtChange, onPriorityChange, onDifficultyChange, onCustomFieldValueChange, customFields, onDeleteTask, onDuplicateTask, onCreateParallelMission, onInsertMissionBefore, onSetStatusAutoSync,
    onDeliverablesChange, onInputsChange, onArchiveTask, onUnarchiveTask, onDependencyChange,
    onDecisionAnswerChange, onDecisionOptionsChange,
    copyCard, duplicateCard, onAddStatus, onDeleteStatus, onRenameStatus, editingCardId, handleFinishTitleEdit,
    currentView, customViews, activeCustomViewId, onViewChange, onSelectCustomView, onGoToTaskInView,
    onSubmitDeliverable, onEditDeliverables, onClearDeliverable, onNavigateToParentMission,
  ]);

  return {
    cardHandlerProps,
    editingCardId,
    setEditingCardId,
  };
};

export default useCardHandlerProps;

